import { Route, Router, Switch, useLocation } from "wouter";
import { Toaster } from "@/components/ui/sonner";
import GameView from "@/components/GameView";
import SettingsView from "@/components/SettingsView";
import HistoryView from "@/components/HistoryView";
import InstallPrompt from "@/components/InstallPrompt";
import ErrorBoundary from "@/components/ErrorBoundary";
import { STORAGE_KEYS } from "@/lib/constants";
import { DEFAULT_SETTINGS } from "@/types/game";
import { useEffect, useState } from "react";

function App() {
  const [location] = useLocation();
  const [theme, setTheme] = useState<'green' | 'black'>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.SETTINGS);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.theme || 'green';
      }
    } catch (err) {
      console.error('Failed to load theme:', err);
    }
    return 'green';
  });

  const readTheme = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.SETTINGS);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.theme || 'green';
      }
    } catch (err) {
      console.error('Failed to load theme:', err);
    }
    return 'green';
  };

  useEffect(() => {
    // Apply theme class to document element
    document.documentElement.classList.remove('theme-green', 'theme-black');
    document.documentElement.classList.add(`theme-${theme}`);
  }, [theme]);

  // Re-read theme from localStorage on navigation (SettingsView saves then navigates back)
  useEffect(() => {
    const newTheme = readTheme();
    if (newTheme !== theme) {
      setTheme(newTheme);
    }
  }, [location]);

  // Listen for theme changes from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEYS.SETTINGS && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          if (parsed.theme && parsed.theme !== theme) {
            setTheme(parsed.theme);
          }
        } catch (err) {
          console.error('Failed to parse settings change:', err);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [theme]);

  return (
    <ErrorBoundary>
      <Router base="/">
        <div className="min-h-[100dvh]" style={{ background: 'var(--app-background)', paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
          <Switch>
            <Route path="/" component={GameView} />
            <Route path="/settings" component={SettingsView} />
            <Route path="/history" component={HistoryView} />
            <Route>
              <div className="min-h-[100dvh] p-4 flex items-center justify-center">
                <div className="text-center text-on-dark">
                  <h1 className="text-2xl font-bold mb-4">Page Not Found</h1>
                  <p className="mb-4">Sorry, this page doesn't exist.</p>
                  <a href="/" className="text-on-dark underline">Return to game</a>
                </div>
              </div>
            </Route>
          </Switch>
          <Toaster />
          <InstallPrompt />
        </div>
      </Router>
    </ErrorBoundary>
  );
}

export default App;